import { apiClient, type PageData } from './client'
import type { GiftProduct } from './gift-products'
import type { DepartmentScope } from '@/types/department'

export interface CustomerGiftUser {
	id: number
	username: string
	nickname: string
}

export interface CustomerGift {
	id: number
	product_id: number
	user_id: number
	sender: string
	sender_mobile: string
	sender_address: string
	receiver: string
	receiver_mobile: string
	receiver_address: string
	number: number
	type: string
	code: string
	remark: string
	record_date: string
	created_at: string
	updated_at: string
	user?: CustomerGiftUser | null
	product?: GiftProduct | null
}

export interface CustomerGiftListParams {
	page?: number
	page_size?: number
	sender?: string
	sender_mobile?: string
	receiver?: string
	receiver_mobile?: string
	product_id?: number
	code?: string
	scope?: DepartmentScope
	department_id?: number
	start_date?: string
	end_date?: string
	sort?: string
	order?: string
}

export interface CustomerGiftImportResult {
	success: number
	failed: number
}

const buildSearch = (params: CustomerGiftListParams) => {
	const search = new URLSearchParams()
	if (params.page) search.set('page', String(params.page))
	if (params.page_size) search.set('page_size', String(params.page_size))
	if (params.sender) search.set('sender', params.sender)
	if (params.sender_mobile) search.set('sender_mobile', params.sender_mobile)
	if (params.receiver) search.set('receiver', params.receiver)
	if (params.receiver_mobile) search.set('receiver_mobile', params.receiver_mobile)
	if (params.product_id) search.set('product_id', String(params.product_id))
	if (params.code) search.set('code', params.code)
	if (params.scope) search.set('scope', params.scope)
	if (params.department_id) search.set('department_id', String(params.department_id))
	if (params.start_date) search.set('start_date', params.start_date)
	if (params.end_date) search.set('end_date', params.end_date)
	if (params.sort) search.set('sort', params.sort)
	if (params.order) search.set('order', params.order)
	return search
}

const uploadExcel = async (url: string, file: File) => {
	const formData = new FormData()
	formData.append('excel', file)
	const response = await apiClient.upload<CustomerGiftImportResult>(url, formData)
	return response.data
}

export const customerGiftsApi = {
	list: async (params: CustomerGiftListParams) => {
		const response = await apiClient.get<PageData<CustomerGift>>(`/customer-gifts?${buildSearch(params)}`)
		return response.data
	},

	template: async () => {
		await apiClient.download('/customer-gifts/template', undefined, { method: 'GET' })
	},

	import: async (file: File) => uploadExcel('/customer-gifts/import', file),

	updateShippingImport: async (file: File) => uploadExcel('/customer-gifts/update-shipping-import', file),

	export: async (selected: number[]) => {
		await apiClient.download('/customer-gifts/export', { selected })
	},

	delete: async (id: number) => {
		await apiClient.delete(`/customer-gifts/${id}`)
	},

	batchDelete: async (selected: number[]) => {
		await apiClient.post('/customer-gifts/batch-delete', { selected })
	},
}
